import React, { useState } from 'react';
import { CtaFragment, HeroCarouselFragment } from '@src/graphql/gql-types';
import styles from './HeroCarousel.module.scss';
import { COLOR_LIME, COLOR_WHITE } from '@src/types/colors';
import MediaImage from '@src/components/MediaImage';
import { WordDictionary } from '@src/types/dictionary';
import { replaceStringWithDictionary } from '@src/utils/strings';
import { Carousel } from 'react-responsive-carousel';
import { ButtonIcon } from '../Button';
import HeroContent from '../HeroContent';
import Visibility from '@src/elements/Visibility';
import { Container } from '@src/elements/Grid';
import { SunraySVG } from '@src/svgs';

type HeroCarousel = HeroCarouselFragment & {
  replacementDictionary?: WordDictionary;
  displayTourBookingButton?: boolean;
  isCommunity?: boolean;
};

const HeroCarousel: React.FC<HeroCarousel> = ({
  replacementDictionary,
  slidesCollection,
  isCommunity,
  displayTourBookingButton
}) => {
  const [selected, setSelected] = useState<number>(0);

  const previousImage = () => {
    if (selected === 0) {
      setSelected((slidesCollection?.items?.length || 0) - 1);
    } else {
      setSelected(selected - 1);
    }
  };

  const nextImage = () => {
    if (selected === (slidesCollection?.items?.length || 0) - 1) {
      setSelected(0);
    } else {
      setSelected(selected + 1);
    }
  };

  return (
    <>
      <div className={styles.hero}>
        <Carousel
          showArrows={false}
          showStatus={false}
          showIndicators={false}
          preventMovementUntilSwipeScrollTolerance={true}
          swipeScrollTolerance={48}
          infiniteLoop={true}
          showThumbs={false}
          selectedItem={selected}
          onChange={setSelected}
          autoPlay={true}
          interval={4000}
        >
          {slidesCollection?.items.map((slide, index) => (
            <div key={slide?.sys?.id} className={styles.heroImage}>
              {slide?.image && (
                <MediaImage {...slide?.image} priority={index === 0} />
              )}
            </div>
          ))}
        </Carousel>

        <div className={styles.buttonContainerWrapper}>
          <Container>
            <div className={styles.buttonContainer}>
              <ButtonIcon
                boxIcon="left-arrow-alt"
                color={COLOR_WHITE}
                fillVariant="filled"
                onClick={previousImage}
                aria-label="Previous Image"
              />
              <ButtonIcon
                boxIcon="right-arrow-alt"
                color={COLOR_WHITE}
                fillVariant="filled"
                onClick={nextImage}
                aria-label="Next Image"
              />
            </div>
          </Container>
        </div>

        <Visibility xs={false} md>
          <div
            className={styles.heroContentDesktop}
            key={`hero-content-${selected}`}
          >
            <Container className={styles.heroContentContainer}>
              <div className={styles.heroContentLargeCard}>
                <HeroContent
                  primaryCta={
                    slidesCollection?.items[selected]?.primaryCta as CtaFragment
                  }
                  secondaryCta={
                    slidesCollection?.items[selected]
                      ?.secondaryCta as CtaFragment
                  }
                  heading={
                    replacementDictionary
                      ? replaceStringWithDictionary(
                          slidesCollection?.items[selected]?.heading,
                          replacementDictionary
                        ) || ''
                      : slidesCollection?.items[selected]?.heading || ''
                  }
                  description={
                    replacementDictionary
                      ? replaceStringWithDictionary(
                          slidesCollection?.items[selected]?.description,
                          replacementDictionary
                        ) || ''
                      : slidesCollection?.items[selected]?.description || ''
                  }
                  displayTourBookingButton={displayTourBookingButton}
                  isCommunity={isCommunity}
                />
                <div className={styles.sunrayContainer}>
                  <SunraySVG color={COLOR_LIME} />
                </div>
              </div>
            </Container>
          </div>
        </Visibility>
      </div>

      <Visibility xs={true} md={false}>
        <div className={styles.heroContentMobile}>
          <Container className={styles.heroContentMobileContainer}>
            <HeroContent
              primaryCta={
                slidesCollection?.items[selected]?.primaryCta as CtaFragment
              }
              secondaryCta={
                slidesCollection?.items[selected]?.secondaryCta as CtaFragment
              }
              heading={slidesCollection?.items[selected]?.heading || ''}
              description={slidesCollection?.items[selected]?.description || ''}
              displayTourBookingButton={displayTourBookingButton}
              isCommunity={isCommunity}
            />
            <div className={styles.sunrayContainerMobile}>
              <div className={styles.sunrayContainer}>
                <SunraySVG color={COLOR_LIME} />
              </div>
            </div>
          </Container>
        </div>
      </Visibility>
    </>
  );
};

export default HeroCarousel;
